import { dom } from "../dom";
import { type IfcTreeNode, state } from "../state";
import { getGeometryExpressIds } from "../fragments/helpers";
import { ifcLoader } from "../viewer/scene";
import { getIfcName } from "../ifc/props";
import { rebuildMeshVisibility } from "../ifc/filters";
import {
  refreshSelectionInfo,
  updatePickHighlight,
  updateVisibilityButtons,
} from "./selection";
import { focusOnExpressIds } from "./viewerCamera";
import { refreshPropertiesPanel } from "./properties";
import { setStatus } from "../ui/status";
import { escapeHtmlAttr, escapeHtmlText } from "../utils/html";

const collapsedNodeIds = new Set<number>();

// Контейнерные узлы Fragments (IFCPROJECT/IFCSITE и т.п.) часто имеют localId === null.
// Чтобы такой узел (и всё дерево под ним) не отбрасывался, выдаём ему синтетический
// отрицательный id, не пересекающийся с реальными localId.
let synthIdSeq = -1;

function getGeometryExpressIdsSet(): Set<number> {
  return new Set(getGeometryExpressIds());
}

function spatialNodeId(node: any): number {
  const id = Number(node?.localId ?? node?.expressID);
  return Number.isFinite(id) ? id : -1;
}

function spatialNodeType(node: any): string {
  return String(node?.category ?? node?.type ?? "").toUpperCase();
}

const SPATIAL_TYPES = new Set([
  "IFCPROJECT",
  "IFCSITE",
  "IFCBUILDING",
  "IFCBUILDINGSTOREY",
  "IFCSPACE",
  "IFCZONE",
]);

async function buildTreeFromSpatial(
  node: any,
  geometryIds: Set<number>,
): Promise<IfcTreeNode | null> {
  if (!node || typeof node !== "object") {
    return null;
  }
  const childrenRawCount = Array.isArray(node.children) ? node.children.length : 0;
  let expressId = spatialNodeId(node);
  if (expressId <= 0) {
    // Узел без валидного localId: если у него есть дети — это контейнер,
    // оставляем его с синтетическим id; иначе пропускаем.
    if (childrenRawCount === 0) {
      return null;
    }
    expressId = synthIdSeq--;
  }
  let type = spatialNodeType(node);
  if (!type) {
    type = (state.elementMeta.get(expressId)?.type || "").toUpperCase();
  }

  // Для листовых элементов используем уже посчитанный кеш (mark), это в 100+
  // раз быстрее, чем тянуть props через worker для каждого узла.
  let name = "";
  const cached = state.elementMeta.get(expressId);
  if (cached?.mark) {
    name = cached.mark;
  } else if (SPATIAL_TYPES.has(type) || !cached) {
    try {
      const props = await ifcLoader.ifcManager.getItemProperties(
        state.ifcModel.modelID,
        expressId,
        false,
      );
      name = getIfcName(props) || "";
      if ((!name || name === "Без имени") && typeof (props as any)?.LongName?.value === "string") {
        name = (props as any).LongName.value;
      }
    } catch (_) {
      name = "";
    }
  }
  if (!name) {
    name = `${type || "Node"} #${expressId}`;
  }

  const childrenRaw = Array.isArray(node.children) ? node.children : [];
  const children: IfcTreeNode[] = [];
  for (const child of childrenRaw) {
    const built = await buildTreeFromSpatial(child, geometryIds);
    if (built) {
      children.push(built);
    }
  }

  const hasGeometry = geometryIds.has(expressId) || children.some((c) => c.hasGeometry);
  return { expressId, type, name, children, hasGeometry };
}

export async function buildIfcTree(): Promise<void> {
  collapsedNodeIds.clear();
  synthIdSeq = -1;
  if (!state.ifcModel) {
    state.ifcTree = null;
    state.treeVisibilityOverrides.clear();
    renderIfcTree();
    return;
  }
  try {
    const spatial = await ifcLoader.ifcManager.getSpatialStructure(state.ifcModel.modelID, true);
    const geometryIds = getGeometryExpressIdsSet();
    const tree = await buildTreeFromSpatial(spatial, geometryIds);
    state.ifcTree = tree;
  } catch (error) {
    console.warn("Не удалось построить дерево IFC:", error);
    state.ifcTree = null;
  }
  state.treeVisibilityOverrides.clear();
  renderIfcTree();
}

function collectDescendantExpressIds(node: IfcTreeNode, out: number[] = []): number[] {
  out.push(node.expressId);
  for (const child of node.children) {
    collectDescendantExpressIds(child, out);
  }
  return out;
}

function collectDescendantGeometryIds(node: IfcTreeNode, out: Set<number>): void {
  if (node.hasGeometry && node.children.length === 0) {
    out.add(node.expressId);
  }
  for (const child of node.children) {
    collectDescendantGeometryIds(child, out);
  }
  if (node.hasGeometry && node.children.length === 0) {
    return;
  }
  if (node.hasGeometry) {
    out.add(node.expressId);
  }
}

function nodeChecked(node: IfcTreeNode): boolean {
  const ids = collectDescendantExpressIds(node);
  for (const id of ids) {
    if (state.hiddenExpressIds.has(id)) {
      return false;
    }
  }
  return true;
}

function renderTreeNode(node: IfcTreeNode): string {
  const isLeaf = node.children.length === 0;
  const isCollapsed = collapsedNodeIds.has(node.expressId);
  const isSelected = state.selectedExpressIds.has(node.expressId);
  const checked = nodeChecked(node);
  const labelText = escapeHtmlText(node.name);
  const typeText = escapeHtmlText(node.type);
  const toggleSymbol = isLeaf ? "" : isCollapsed ? "▶" : "▼";
  const childrenHtml = node.children
    .map((child) => renderTreeNode(child))
    .join("");
  return (
    `<div class="tree-node${isCollapsed ? " is-collapsed" : ""}" data-express-id="${node.expressId}">` +
    `<div class="tree-node-row${isSelected ? " is-selected" : ""}" data-row-id="${node.expressId}">` +
    `<button class="tree-node-toggle${isLeaf ? " is-leaf" : ""}" type="button" data-toggle-id="${node.expressId}" tabindex="-1">${toggleSymbol}</button>` +
    `<input class="tree-node-checkbox" type="checkbox" ${checked ? "checked" : ""} data-check-id="${node.expressId}" />` +
    `<span class="tree-node-type">${typeText}</span>` +
    `<span class="tree-node-label" title="${escapeHtmlAttr(node.name)}">${labelText}</span>` +
    `</div>` +
    `<div class="tree-node-children">${childrenHtml}</div>` +
    `</div>`
  );
}

export function renderIfcTree(): void {
  const hasTree = Boolean(state.ifcTree);
  dom.treeExpandAllButton.disabled = !hasTree;
  dom.treeCollapseAllButton.disabled = !hasTree;
  if (!hasTree || !state.ifcTree) {
    dom.treeRoot.innerHTML =
      '<p class="muted" style="margin:0; font-size:12px;">Загрузите IFC для просмотра дерева.</p>';
    return;
  }
  dom.treeRoot.innerHTML = renderTreeNode(state.ifcTree);
}

function setNodeVisibilityRecursive(node: IfcTreeNode, visible: boolean): void {
  if (node.children.length === 0) {
    if (visible) {
      state.hiddenExpressIds.delete(node.expressId);
    } else {
      state.hiddenExpressIds.add(node.expressId);
    }
    return;
  }
  for (const child of node.children) {
    setNodeVisibilityRecursive(child, visible);
  }
  if (node.hasGeometry) {
    if (visible) {
      state.hiddenExpressIds.delete(node.expressId);
    } else {
      state.hiddenExpressIds.add(node.expressId);
    }
  }
}

function findNodeById(root: IfcTreeNode | null, expressId: number): IfcTreeNode | null {
  if (!root) {
    return null;
  }
  if (root.expressId === expressId) {
    return root;
  }
  for (const child of root.children) {
    const found = findNodeById(child, expressId);
    if (found) {
      return found;
    }
  }
  return null;
}

function toggleCollapse(expressId: number): void {
  if (collapsedNodeIds.has(expressId)) {
    collapsedNodeIds.delete(expressId);
  } else {
    collapsedNodeIds.add(expressId);
  }
  renderIfcTree();
}

export function expandAllTree(): void {
  collapsedNodeIds.clear();
  renderIfcTree();
}

export function collapseAllTree(): void {
  if (!state.ifcTree) {
    return;
  }
  const all: number[] = [];
  collectDescendantExpressIds(state.ifcTree, all);
  collapsedNodeIds.clear();
  for (const id of all) {
    const node = findNodeById(state.ifcTree, id);
    if (node && node.children.length > 0) {
      collapsedNodeIds.add(id);
    }
  }
  if (state.ifcTree) {
    collapsedNodeIds.delete(state.ifcTree.expressId);
  }
  renderIfcTree();
}

async function selectNodeAndDescendants(node: IfcTreeNode, multi: boolean): Promise<void> {
  if (!state.ifcModel) {
    return;
  }
  const leafIds = new Set<number>();
  collectDescendantGeometryIds(node, leafIds);
  if (leafIds.size === 0 && node.hasGeometry) {
    leafIds.add(node.expressId);
  }
  if (leafIds.size === 0) {
    return;
  }
  if (!multi) {
    state.selectedExpressIds.clear();
  }
  for (const id of leafIds) {
    state.selectedExpressIds.add(id);
  }
  updatePickHighlight();
  updateVisibilityButtons();
  await refreshSelectionInfo();
  await refreshPropertiesPanel();
  renderIfcTree();
}

export async function handleTreeClick(event: MouseEvent): Promise<void> {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }
  const toggleBtn = target.closest<HTMLElement>("[data-toggle-id]");
  if (toggleBtn) {
    const id = Number(toggleBtn.dataset.toggleId);
    if (Number.isFinite(id)) {
      toggleCollapse(id);
    }
    return;
  }
  const checkbox = target.closest<HTMLInputElement>("input[data-check-id]");
  if (checkbox) {
    const id = Number(checkbox.dataset.checkId);
    if (!Number.isFinite(id)) {
      return;
    }
    const node = findNodeById(state.ifcTree, id);
    if (!node) {
      return;
    }
    setNodeVisibilityRecursive(node, checkbox.checked);
    rebuildMeshVisibility();
    renderIfcTree();
    setStatus(
      checkbox.checked
        ? `Показаны: ${node.name}`
        : `Скрыты: ${node.name}. Всего скрыто: ${state.hiddenExpressIds.size}`,
    );
    return;
  }
  const row = target.closest<HTMLElement>("[data-row-id]");
  if (row) {
    const id = Number(row.dataset.rowId);
    if (!Number.isFinite(id)) {
      return;
    }
    const node = findNodeById(state.ifcTree, id);
    if (!node) {
      return;
    }
    await selectNodeAndDescendants(node, event.ctrlKey || event.metaKey || state.multiSelectMode);
  }
}

export async function handleTreeDoubleClick(event: MouseEvent): Promise<void> {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }
  const row = target.closest<HTMLElement>("[data-row-id]");
  if (!row) {
    return;
  }
  const id = Number(row.dataset.rowId);
  if (!Number.isFinite(id)) {
    return;
  }
  const node = findNodeById(state.ifcTree, id);
  if (!node) {
    return;
  }
  const ids = new Set<number>();
  collectDescendantGeometryIds(node, ids);
  if (ids.size === 0 && node.hasGeometry) {
    ids.add(node.expressId);
  }
  if (ids.size === 0) {
    return;
  }
  focusOnExpressIds([...ids]);
}
